import React from "react";
import { Card, Typography, Row, Col, Button, Tag } from "antd";
import {
  CalculatorOutlined,
  ContainerOutlined,
  TeamOutlined,
  CheckSquareOutlined,
  UserOutlined,
  GlobalOutlined,
  LineChartOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import styled from "@emotion/styled";
import { trackFeatureUsage } from "../lib/posthog";
import { AdminNavBar } from "../components/AdminNavBar";

const { Title } = Typography;

const Container = styled.div`
  padding: 24px;
`;

const AdminCard = styled(Card)`
  height: 100%;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-5px);
  }

  .ant-card-body {
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const IconWrapper = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: #1890ff;
`;

const CardTitle = styled(Title)`
  margin-top: 0 !important;
  text-align: center;
`;

const CardDescription = styled.p`
  text-align: center;
  margin-bottom: 24px;
  flex-grow: 1;
`;

const WipTag = styled(Tag)`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();

  const handleNavigate = (path: string, feature: string) => {
    navigate(path);
    trackFeatureUsage(`admin_${feature}`);
  };

  const adminSections = [
    {
      title: "Bookings",
      icon: <ContainerOutlined />,
      description: "Manage and track customer bookings",
      path: "/admin/bookings",
      feature: "bookings",
    },
    {
      title: "Containers",
      icon: <ContainerOutlined />,
      description: "Manage shipping containers and consolidations",
      path: "/admin/containers",
      feature: "containers",
    },
    {
      title: "Tariffs",
      icon: <ContainerOutlined />,
      description: "Configure shipping tariffs, rates, and pricing brackets",
      path: "/admin/tariffs",
      feature: "tariffs",
    },
    {
      title: "Companies",
      icon: <TeamOutlined />,
      description:
        "Manage companies, view and assign users, change company status",
      path: "/admin/companies",
      feature: "companies",
    },
    {
      title: "Tasks",
      icon: <CheckSquareOutlined />,
      description: "Manage automated and manual tasks for shipments",
      path: "/admin/tasks",
      feature: "tasks",
    },
    {
      title: "Users",
      icon: <UserOutlined />,
      description: "User management, permissions, and account settings",
      path: "/admin/users",
      feature: "users",
    },
    {
      title: "Ports",
      icon: <GlobalOutlined />,
      description: "Manage shipping ports, locations, and related data",
      path: "/admin/ports",
      feature: "ports",
    },
    {
      title: "Invoice Settings",
      icon: <CalculatorOutlined />,
      description: "Manage invoicing and billing for shipping services",
      path: "/admin/pricing",
      feature: "pricing",
      wip: true,
    },
    {
      title: "Reports",
      icon: <LineChartOutlined />,
      description: "View system reports and analytics",
      path: "/admin/reports",
      feature: "reports",
      wip: true,
    },
    {
      title: "Invoices",
      icon: <CalculatorOutlined />,
      description: "Manage customer invoices and payments",
      path: "/admin/invoices",
      feature: "invoices",
      wip: true,
    },
  ];

  return (
    <Container>
      <AdminNavBar />

      <Title level={2}>Admin Dashboard</Title>

      <Row gutter={[24, 24]}>
        {adminSections.map((section) => (
          <Col xs={24} sm={12} md={8} key={section.path}>
            <AdminCard
              onClick={() => handleNavigate(section.path, section.feature)}
            >
              {section.wip && <WipTag color="warning">Work in progress</WipTag>}
              <IconWrapper>{section.icon}</IconWrapper>
              <CardTitle level={4}>{section.title}</CardTitle>
              <CardDescription>{section.description}</CardDescription>
              <Button type="primary">Go to {section.title}</Button>
            </AdminCard>
          </Col>
        ))}
      </Row>
    </Container>
  );
};

export default AdminDashboard;
